import { useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Icon, IconButton, Menu, Text } from '@zillow/constellation-10';
import { IconDeleteOutline, IconEditOutline, IconMoreFilled } from '@zillow/constellation-icons';

import { PartialMessage } from '@zg-rentals/backend-styled-components';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { getListingStatusSuffix } from '../../../../utils/clickstreamUtils';

import { TRIGGER_LOCATIONS_MAP } from '../../../../constants/ClickstreamConstants';

import CostsAndFeesDeleteFeeModal from '../../../modals/CostsAndFeesDeleteFeeModal';

import { singleTextLineStyles } from '../../../../styles/text';

import { emitClickDeleteSFFeeEvent, EVENT_IDS } from '../../../../analytics/events/singleFamilyCostsAndFees';
import { useAuth } from '../../../AuthProvider';

import type React from 'react';
import type {
  GetSingleFamilyFeesDictionaryItemsDto,
  PartialMessageTextAndUrlDto,
  SingleFamilyFeeDto,
} from '@zg-rentals/ts-rental-manager-api-client';

type FeeListItemProps = {
  fee: SingleFamilyFeeDto;
  dictionaryItems: GetSingleFamilyFeesDictionaryItemsDto;
  handleEditFeeClick: (fee: SingleFamilyFeeDto) => void;
  displayedLocation: string;
};

type FeeDisplayObject = {
  feeName: string;
  feeRequirementType: string;
  feeRefundableType: string;
  feeAmountDisplay: React.ReactNode;
};

const getFeeAmountDisplay = (partialMessages: Array<PartialMessageTextAndUrlDto>, textStyle: 'body' | 'body-sm') => {
  return (
    <Text asChild>
      <span>
        <PartialMessage fontType={textStyle} partialMessages={partialMessages} />
      </span>
    </Text>
  );
};

const getFeeNameDisplay = (fee: SingleFamilyFeeDto, dictionaryItems: GetSingleFamilyFeesDictionaryItemsDto) => {
  if (Object.keys(dictionaryItems?.mainCategories).includes(fee.feeCategory)) {
    return dictionaryItems?.mainCategories[fee?.feeCategory]?.feeType[fee?.feeType]?.displayName;
  } else {
    return dictionaryItems?.otherCategories.categories[fee?.feeCategory]?.feeType[fee?.feeType]?.displayName;
  }
};

export const FeeListItem: React.FC<FeeListItemProps> = ({
  fee,
  dictionaryItems,
  handleEditFeeClick,
  displayedLocation,
}) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { user } = useAuth();
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const [isDeleteFeeModalOpen, setIsDeleteFeeModalOpen] = useState(false);

  const textStyle = displayedLocation === TRIGGER_LOCATIONS_MAP.posting_path_review ? 'body' : 'body-sm';

  const triggerLocation = useMemo(() => {
    const mappedLocation = TRIGGER_LOCATIONS_MAP[displayedLocation as keyof typeof TRIGGER_LOCATIONS_MAP];
    const listingStatusSuffix = getListingStatusSuffix(
      listingDetails?.active ?? false,
      listingDetails?.lastActivatedMs?.toString() ?? '',
    );
    return `${mappedLocation}${listingStatusSuffix}`;
  }, [listingDetails?.active, listingDetails?.lastActivatedMs, displayedLocation]);

  const displayObject: FeeDisplayObject = {
    feeName: getFeeNameDisplay(fee, dictionaryItems) ?? '',
    feeRequirementType: dictionaryItems?.feeRequirementType[fee?.feeRequirementType]?.displayName ?? '',
    feeRefundableType: dictionaryItems?.feeRefundableType[fee?.feeRefundableType]?.displayName ?? '',
    feeAmountDisplay: getFeeAmountDisplay(fee?.styledComponents?.feeCalculationAndFrequencyDisplay, textStyle),
  };

  const handleDeleteClick = () => {
    setIsDeleteFeeModalOpen(true);
    if (listingDetails && user) {
      emitClickDeleteSFFeeEvent({ currentProperty: listingDetails, user, triggerLocation });
    }
  };

  return (
    <>
      <Box
        css={{
          marginBlock: '400',
          marginLeft: displayedLocation === TRIGGER_LOCATIONS_MAP.posting_path_review ? '200' : 'none',
        }}
      >
        <Box css={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
          <Box css={{ marginBottom: '200', minWidth: 0, flex: 1 }}>
            <Text textStyle={textStyle === 'body' ? 'body-bold' : 'body-sm-bold'}>{displayObject.feeName}</Text>
            {fee?.feeDescription && (
              <Text
                textStyle={textStyle}
                css={{
                  ...singleTextLineStyles,
                  marginTop: '100',
                  color: 'text.subtle',
                }}
              >
                {fee.feeDescription}
              </Text>
            )}
            <Box
              css={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-start',
                marginTop: fee?.feeDescription ? '200' : undefined,
              }}
            >
              <Text textStyle={textStyle}>{displayObject.feeAmountDisplay}</Text>
              <Text textStyle={textStyle} css={{ paddingInline: '200' }}>
                |
              </Text>
              <Text textStyle={textStyle}>{displayObject.feeRequirementType}</Text>
              {fee?.feeRefundableType && (
                <>
                  <Text textStyle={textStyle} css={{ paddingInline: '200' }}>
                    |
                  </Text>
                  <Text textStyle={textStyle}>{displayObject.feeRefundableType}</Text>
                </>
              )}
            </Box>
          </Box>
          <div>
            <Menu
              trigger={
                <IconButton
                  tone="neutral"
                  emphasis="bare"
                  data-testid="FeeListItem-MenuTrigger"
                  title="Fee options"
                  icon={<IconMoreFilled />}
                />
              }
              content={
                <>
                  <Menu.Item onClick={() => handleEditFeeClick(fee)} data-eventid={EVENT_IDS.CLICK_EDIT_SF_FEE}>
                    <Menu.ItemIcon>
                      <Icon render={<IconEditOutline />} />
                    </Menu.ItemIcon>
                    <Menu.ItemLabel>Edit</Menu.ItemLabel>
                  </Menu.Item>
                  <Menu.Item
                    onClick={handleDeleteClick}
                    data-testid="FeeListItem-DeleteMenuItem"
                    data-eventid={EVENT_IDS.CLICK_DELETE_SF_FEE}
                  >
                    <Menu.ItemIcon>
                      <Icon render={<IconDeleteOutline />} />
                    </Menu.ItemIcon>
                    <Menu.ItemLabel>Delete</Menu.ItemLabel>
                  </Menu.Item>
                </>
              }
            />
          </div>
        </Box>
      </Box>

      {isDeleteFeeModalOpen && (
        <CostsAndFeesDeleteFeeModal
          feeId={fee.costAndFeeId}
          setShowDeleteFeeModal={setIsDeleteFeeModalOpen}
          isSingleFamilyFee={true}
        />
      )}
    </>
  );
};
